import { Link } from "@tanstack/react-router";
import { ArrowDown, ArrowUp, Download } from "lucide-react";
import { ProjectProgressBar } from "@/components/projects/ProjectProgressBar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { Milestone } from "@/hooks/use-milestones";
import type { Project, ProjectLifecyclePhase } from "@/hooks/use-projects";
import { PROJECT_PHASE_LABELS } from "@/hooks/use-projects";
import { downloadCsv } from "@/lib/csv";
import { cn } from "@/lib/utils";

const PROJECT_TYPE_LABELS: Record<string, string> = {
  general_contracting: "General Contracting",
  cmar: "CMAR",
  design_build: "Design-Build",
  owner_managed: "Owner Managed",
};

function formatCurrency(value: string | null): string {
  if (!value) return "--";
  const num = Number(value);
  if (Number.isNaN(num)) return "--";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(num);
}

function statusVariant(
  status: string,
): "success" | "warning" | "secondary" | "destructive" | "default" {
  switch (status) {
    case "active":
      return "success";
    case "draft":
    case "pending":
      return "warning";
    case "completed":
    case "closed":
      return "secondary";
    case "cancelled":
    case "on_hold":
      return "destructive";
    default:
      return "default";
  }
}

function phaseBadgeClass(phase: ProjectLifecyclePhase): string {
  switch (phase) {
    case "pre_construction":
      return "border-transparent bg-amber-500/15 text-amber-700";
    case "design":
      return "border-transparent bg-sky-500/15 text-sky-700";
    case "construction":
      return "border-transparent bg-blue-500/15 text-blue-700";
    case "post_construction":
      return "border-transparent bg-violet-500/15 text-violet-700";
    case "completed":
      return "border-transparent bg-gray-400/20 text-gray-600";
  }
}

export type ProjectsSortField = "name" | "type" | "location" | "status" | "phase" | "contractValue";
type SortDir = "asc" | "desc";

interface ProjectsDataTableProps {
  projects: Project[];
  milestonesByProject: Record<string, Milestone[]>;
  sortField: ProjectsSortField;
  sortDir: SortDir;
  onSortChange: (field: ProjectsSortField) => void;
}

interface ColumnDef {
  field: ProjectsSortField | null;
  label: string;
  className?: string;
}

const COLUMNS: ColumnDef[] = [
  { field: "name", label: "Project" },
  { field: "type", label: "Delivery Method" },
  { field: "location", label: "Location" },
  { field: "status", label: "Status" },
  { field: "phase", label: "Phase" },
  { field: "contractValue", label: "Contract Value", className: "text-right" },
  { field: null, label: "Progress" },
];

export function ProjectsDataTable({
  projects,
  milestonesByProject,
  sortField,
  sortDir,
  onSortChange,
}: ProjectsDataTableProps) {
  const handleExport = () => {
    const rows = projects.map((p) => ({
      name: p.name,
      projectNumber: p.projectNumber ?? "",
      deliveryMethod: PROJECT_TYPE_LABELS[p.type] ?? p.type,
      location: p.location ?? "",
      status: p.status,
      phase: PROJECT_PHASE_LABELS[p.phase],
      contractValue: p.contractValue ?? "",
      client: p.client ?? "",
      projectManager: p.projectManager ?? "",
    }));
    downloadCsv("projects", rows, [
      { key: "name", label: "Project" },
      { key: "projectNumber", label: "Project Number" },
      { key: "deliveryMethod", label: "Delivery Method" },
      { key: "location", label: "Location" },
      { key: "status", label: "Status" },
      { key: "phase", label: "Phase" },
      { key: "contractValue", label: "Contract Value" },
      { key: "client", label: "Client" },
      { key: "projectManager", label: "Project Manager" },
    ]);
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={handleExport} disabled={projects.length === 0}>
          <Download className="mr-2 h-4 w-4" />
          Export CSV
        </Button>
      </div>
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              {COLUMNS.map((col) => {
                const active = col.field !== null && sortField === col.field;
                const sortable = col.field !== null;
                return (
                  <th
                    key={col.label}
                    className={cn(
                      "px-4 py-3 text-left text-xs font-medium text-muted-foreground",
                      col.className,
                    )}
                  >
                    {sortable ? (
                      <button
                        type="button"
                        onClick={() => {
                          if (col.field) onSortChange(col.field);
                        }}
                        className={cn(
                          "inline-flex items-center gap-1 hover:text-foreground",
                          col.className?.includes("text-right") && "ml-auto",
                        )}
                      >
                        <span>{col.label}</span>
                        {active ? (
                          sortDir === "asc" ? (
                            <ArrowUp className="h-3 w-3" />
                          ) : (
                            <ArrowDown className="h-3 w-3" />
                          )
                        ) : null}
                      </button>
                    ) : (
                      col.label
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {projects.length === 0 ? (
              <tr>
                <td
                  colSpan={COLUMNS.length}
                  className="px-4 py-8 text-center text-muted-foreground"
                >
                  No projects match your filters.
                </td>
              </tr>
            ) : (
              projects.map((project) => (
                <tr
                  key={project.id}
                  className="border-b last:border-b-0 hover:bg-muted/30 transition-colors"
                >
                  <td className="px-4 py-3">
                    <Link
                      to="/projects/$projectId"
                      params={{ projectId: project.id }}
                      search={{ tab: "overview" }}
                      className="font-medium text-foreground hover:underline"
                    >
                      {project.name}
                    </Link>
                    {project.projectNumber ? (
                      <div className="text-xs text-muted-foreground">{project.projectNumber}</div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {PROJECT_TYPE_LABELS[project.type] ?? project.type}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{project.location ?? "--"}</td>
                  <td className="px-4 py-3">
                    <Badge variant={statusVariant(project.status)}>{project.status}</Badge>
                  </td>
                  <td className="px-4 py-3">
                    <Badge className={phaseBadgeClass(project.phase)}>
                      {PROJECT_PHASE_LABELS[project.phase]}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right font-medium">
                    {formatCurrency(project.contractValue)}
                  </td>
                  <td className="px-4 py-3 min-w-[140px]">
                    <ProjectProgressBar milestones={milestonesByProject[project.id] ?? []} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
